import axios from 'axios';

import { WWEX_FREIGHT_PROVIDER_ID, OPERATIONS_MANAGER_CONTACT_TYPE } from './constants';
import type { WwexFormData } from './types';

type OpenAccountRequest = {
  freightProviderId: number;
  sellerName: string;
  paymentMethodId: string;
  billingAddress: {
    line1: string;
    line2?: string;
    line3?: string;
    city: string;
    state: string;
    postalCode: string;
    countryCode?: string;
  };
  shippingAddress: {
    line1: string;
    line2?: string;
    line3?: string;
    city: string;
    state: string;
    postalCode: string;
    countryCode?: string;
  };
  contacts: Array<{
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
    title: string;
    contactType: string;
    externalId: string;
    isBillingContact?: boolean;
    mobilePhone?: string;
  }>;
  primaryContactExternalId: string;
};

type OpenAccountResponse = {
  isSuccess: boolean;
  clientId?: string | null;
  clientSecret?: string | null;
  sellerFreightProviderId: number | null;
  message?: string;
  errors: Array<{ code: string; message: string }> | null;
};

export const openWwexAccount = async (formData: WwexFormData): Promise<OpenAccountResponse> => {
  // Validate required payment method ID
  if (!formData.paymentMethodId) {
    throw new Error('Payment method ID is required');
  }

  // Use billing full name if provided, otherwise construct from first/last
  const billingNameParts = formData.billingFullName?.trim().split(' ') || [];
  const billingFirstName = billingNameParts[0] || formData.firstName;
  const billingLastName = billingNameParts.slice(1).join(' ') || formData.lastName;

  const payload: OpenAccountRequest = {
    freightProviderId: WWEX_FREIGHT_PROVIDER_ID,
    sellerName:
      formData.billingCompanyName ||
      formData.companyName ||
      formData.billingFullName ||
      `${formData.firstName} ${formData.lastName}`.trim(),
    paymentMethodId: formData.paymentMethodId,
    billingAddress: {
      line1: formData.billingAddress.line1,
      line2: formData.billingAddress.line2,
      line3: formData.billingAddress.line3,
      city: formData.billingAddress.city,
      state: formData.billingAddress.state,
      postalCode: formData.billingAddress.postalCode,
      countryCode: formData.billingAddress.countryCode || 'US',
    },
    shippingAddress: {
      line1: formData.shippingAddress.line1,
      line2: formData.shippingAddress.line2,
      line3: formData.shippingAddress.line3,
      city: formData.shippingAddress.city,
      state: formData.shippingAddress.state,
      postalCode: formData.shippingAddress.postalCode,
      countryCode: formData.shippingAddress.countryCode || 'US',
    },
    contacts: [
      {
        email: formData.email,
        firstName: billingFirstName,
        lastName: billingLastName,
        phone: formData.phoneNumber,
        title: formData.title || 'Billing Manager',
        contactType: OPERATIONS_MANAGER_CONTACT_TYPE,
        externalId: 'contact-1',
        isBillingContact: true,
      },
    ],
    primaryContactExternalId: 'contact-1',
  };

  try {
    const response = await axios.post<OpenAccountResponse>('/freight/open-account', payload);
    return response.data;
  } catch (error) {
    // Handle axios errors
    if (axios.isAxiosError(error)) {
      const errorData = error.response?.data as OpenAccountResponse | undefined;
      // Return the error response in the expected format
      return {
        isSuccess: false,
        sellerFreightProviderId: null,
        message: errorData?.message || error.message || 'An error occurred',
        errors: errorData?.errors || [{ code: 'UNKNOWN_ERROR', message: error.message }],
      };
    }

    // Handle non-axios errors
    throw error;
  }
};
